// Geführter Schritt-Streifen für den Dach-Editor (Anfänger-UX).
//
// Zeigt den PV-Planungs-Workflow als 5 klare Schritte mit Ist-Zustand
// (erledigt / aktuell / offen). Die Zustände werden NICHT neu berechnet,
// sondern aus dem bestehenden Editor-State abgeleitet (Gebäude, Dachflächen,
// First/Traufe, Modulbelegung, Prüfampel) und als Booleans hereingereicht.
// Klick auf einen Schritt springt zum passenden Werkzeug — rein führend,
// niemals blockierend (der Nutzer darf jederzeit frei arbeiten).
import type { ReactNode } from "react";

export type StepState = "done" | "current" | "todo" | "warn";

export interface RoofStep {
  key: string;
  /** Kurzlabel, z. B. „Dachfläche". */
  label: string;
  /** Einzeiler-Hinweis für den aktuellen Schritt. */
  hint: string;
  state: StepState;
  /** Optionaler Sprung zum passenden Werkzeug/Panel. */
  onClick?: (() => void) | undefined;
}

const COLORS: Record<StepState, { ring: string; bg: string; fg: string; label: string }> = {
  done: { ring: "#16a34a", bg: "#16a34a", fg: "#fff", label: "#15803d" },
  current: { ring: "#2563eb", bg: "#2563eb", fg: "#fff", label: "#1d4ed8" },
  warn: { ring: "#d97706", bg: "#fff", fg: "#b45309", label: "#b45309" },
  todo: { ring: "#cbd5e1", bg: "#fff", fg: "#94a3b8", label: "#64748b" },
};

function CheckMark(): ReactNode {
  return (
    <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3.5} strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 13l4 4L19 7" />
    </svg>
  );
}

export function RoofStepStrip({ steps }: { steps: RoofStep[] }) {
  // Aktiver Hinweis: erst der aktuelle Schritt, sonst eine offene Warnung.
  const active = steps.find((s) => s.state === "current") ?? steps.find((s) => s.state === "warn");
  return (
    <div
      style={{
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        gap: 0,
        padding: "7px 16px",
        background: "#f8fafc",
        borderBottom: "1px solid #e2e8f0",
        overflowX: "auto",
      }}
    >
      {steps.map((s, i) => {
        const c = COLORS[s.state];
        const num = i + 1;
        return (
          <div key={s.key} style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
            <button
              type="button"
              onClick={s.onClick}
              title={s.hint}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 7,
                background: s.state === "current" ? "#eff6ff" : "transparent",
                border: s.state === "current" ? "1px solid #bfdbfe" : "1px solid transparent",
                borderRadius: 999,
                padding: "3px 10px 3px 4px",
                cursor: s.onClick ? "pointer" : "default",
              }}
            >
              <span
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  fontSize: 12,
                  fontWeight: 700,
                  border: `2px solid ${c.ring}`,
                  background: c.bg,
                  color: c.fg,
                }}
              >
                {s.state === "done" ? <CheckMark /> : s.state === "warn" ? "!" : num}
              </span>
              <span style={{ fontSize: 12, fontWeight: 600, color: c.label, whiteSpace: "nowrap" }}>{s.label}</span>
            </button>
            {i < steps.length - 1 && (
              <span
                style={{
                  width: 22,
                  height: 2,
                  background: steps[i + 1]!.state === "todo" ? "#e2e8f0" : "#cbd5e1",
                  margin: "0 2px",
                  flexShrink: 0,
                }}
              />
            )}
          </div>
        );
      })}
      {active && (
        <span style={{ marginLeft: 14, fontSize: 11.5, color: "#475569", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          → {active.hint}
        </span>
      )}
    </div>
  );
}
